import React, { useRef } from 'react';

interface SparklineProps {
    data: number[];
    width?: number;
    height?: number;
    color?: string;
    fillColor?: string;
    hoveredIndex?: number | null;
    onHoverChange?: (index: number | null) => void;
    showTooltip?: boolean;
}

const Sparkline: React.FC<SparklineProps> = ({
    data,
    width = 60,
    height = 20,
    color = '#10b981',
    fillColor = 'rgba(16, 185, 129, 0.1)',
    hoveredIndex = null,
    onHoverChange,
    showTooltip = true
}) => {
    const svgRef = useRef<SVGSVGElement>(null);

    if (data.length === 0) {
        return (
            <svg width={width} height={height}>
                <line
                    x1="0"
                    y1={height / 2}
                    x2={width}
                    y2={height / 2}
                    stroke="rgba(255, 255, 255, 0.2)"
                    strokeWidth="1"
                    strokeDasharray="2,2"
                />
            </svg>
        );
    }

    const max = Math.max(...data);
    const min = Math.min(...data);
    const range = max - min || 1;

    // Calculate points for the line
    const points = data.map((value, index) => {
        const x = (index / (data.length - 1 || 1)) * width;
        const y = height - ((value - min) / range) * height;
        return { x, y, value };
    });

    const pointsString = points.map(p => `${p.x},${p.y}`).join(' ');

    // Create area fill path
    const areaPath = `
    M 0,${height}
    L ${points.map(p => `${p.x},${p.y}`).join(' L ')}
    L ${width},${height}
    Z
  `;

    const handleMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
        if (!svgRef.current || !onHoverChange) return;

        const rect = svgRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;

        // Find closest data point
        const index = Math.round((x / width) * (data.length - 1));
        const clampedIndex = Math.max(0, Math.min(index, data.length - 1));

        onHoverChange(clampedIndex);
    };

    const handleMouseLeave = () => {
        if (onHoverChange) {
            onHoverChange(null);
        }
    };

    const tooltipPosition = hoveredIndex !== null && points[hoveredIndex]
        ? { x: points[hoveredIndex].x, y: points[hoveredIndex].y }
        : { x: 0, y: 0 };

    return (
        <div style={{ position: 'relative', display: 'inline-block' }}>
            <svg
                ref={svgRef}
                width={width}
                height={height}
                style={{ display: 'block', cursor: 'crosshair' }}
                onMouseMove={handleMouseMove}
                onMouseLeave={handleMouseLeave}
            >
                {/* Fill area */}
                <path
                    d={areaPath}
                    fill={fillColor}
                />

                {/* Line */}
                <polyline
                    points={pointsString}
                    fill="none"
                    stroke={color}
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />

                {/* Hover indicator dot */}
                {hoveredIndex !== null && points[hoveredIndex] && (
                    <circle
                        cx={points[hoveredIndex].x}
                        cy={points[hoveredIndex].y}
                        r="3"
                        fill={color}
                        stroke="white"
                        strokeWidth="1.5"
                    />
                )}
            </svg>

            {/* Tooltip - only show if enabled */}
            {showTooltip && hoveredIndex !== null && data[hoveredIndex] !== undefined && (
                <div
                    style={{
                        position: 'absolute',
                        left: `${tooltipPosition.x}px`,
                        top: `${tooltipPosition.y - 30}px`,
                        transform: 'translateX(-50%)',
                        background: 'rgba(0, 0, 0, 0.9)',
                        color: 'white',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '11px',
                        fontWeight: 600,
                        pointerEvents: 'none',
                        whiteSpace: 'nowrap',
                        zIndex: 1000,
                        border: `1px solid ${color}`,
                    }}
                >
                    {data[hoveredIndex]}ms
                </div>
            )}
        </div>
    );
};

export default Sparkline;
